import React, {useState} from "react";
import {Button} from "primereact/button";

import {formatCurrency, getHostName} from "../../utils/Utils";
import ProductSelectionBar from "./ProductSelectionBar";

const GROUPS = ['auto_matched', 'check', 'ruled_out'];

// Same field-name mapping the compare dialog uses; labels live in suggestion.field.*.
const FIELD_KEY = {'load/speed index': 'load_speed_index', 'oe marking': 'oe_marking'};
const COUNT_CODES = ['no_pattern_on_site', 'multiple_on_site'];

const TONE = {
    green: {bg: '#dcfce7', fg: '#15803d'},
    amber: {bg: '#fef3c7', fg: '#b45309'},
    gray: {bg: '#f3f4f6', fg: '#6b7280'},
};

export const fieldLabel = (t, field) => t('suggestion.field.' + (FIELD_KEY[field] || field), field);

// One human readable line for a reason {field, code, product_value, suggestion_value}.
export const reasonText = (t, reason) => {
    const values = {
        field: fieldLabel(t, reason.field),
        product: reason.product_value,
        suggestion: reason.suggestion_value,
    };
    if (COUNT_CODES.includes(reason.code))
        values.count = reason.suggestion_value;
    // suggestion_value is the URL of the listing that already holds this site.
    if (reason.code === 'host_already_matched')
        values.host = getHostName(reason.suggestion_value);
    return t('suggestion.reason.' + reason.code, values);
};

const outcomeOf = (row) => GROUPS.includes(row.match_outcome) ? row.match_outcome : 'check';

// "year differs (1) · pattern differs (1)" for the collapsed header of the ruled out group.
const reasonSummary = (t, rows) => {
    const counts = new Map();
    rows.forEach(row => (row.match_reasons || []).forEach(reason => {
        if (reason.code === 'all_agree') return;
        const key = reason.code + '|' + reason.field;
        const entry = counts.get(key) || {reason, count: 0};
        entry.count += 1;
        counts.set(key, entry);
    }));
    return Array.from(counts.values())
        .map(({reason, count}) => t('suggestion.reason_short.' + reason.code, {field: fieldLabel(t, reason.field)}) + ' (' + count + ')')
        .join(' · ');
};

const Tag = ({tone, title, children}) => (
    <span className="p-tag" title={title} style={{padding: '.15em .5em', borderRadius: '6px', fontSize: '.8rem', marginLeft: '.5em',
        background: tone.bg, color: tone.fg}}>{children}</span>
);

const SuggestionGroups = ({t, suggestions, onAccept, onReject, onRevert, onRestore, isAdmin}) => {
    const [ruledOutOpen, setRuledOutOpen] = useState(false);
    const [selectedUrls, setSelectedUrls] = useState([]);

    const byGroup = {auto_matched: [], check: [], ruled_out: []};
    (suggestions || []).forEach(row => byGroup[outcomeOf(row)].push(row));

    const isOpen = (name) => name !== 'ruled_out' || ruledOutOpen;
    const undecided = (row) => !row.is_match_decision_made;
    const selectedRows = byGroup.check.filter(row => undecided(row) && selectedUrls.includes(row.product_url));

    const toggleSelected = (row) => setSelectedUrls(urls =>
        urls.includes(row.product_url) ? urls.filter(u => u !== row.product_url) : [...urls, row.product_url]);

    const act = (fn, rows) => {
        fn(rows);
        setSelectedUrls([]);
    };

    // An engine auto-match that is not a competitor (dry run: auto_match_writes off) is still undecided.
    const notTracked = (name, row) => name === 'auto_matched' && !row.is_matched && undecided(row);

    const renderRow = (name, row) => {
        const canDecide = undecided(row);
        const pending = notTracked(name, row);
        const canAccept = canDecide && (name === 'check' || ((name === 'ruled_out' || pending) && isAdmin));
        const canReject = canDecide && (name === 'check' || pending);
        const reasons = row.match_reasons || [];
        return (
            <div key={row.product_url} data-testid="suggestion-row"
                 style={{display: 'flex', alignItems: 'center', gap: '.75em', padding: '.6em .5em', borderTop: '1px solid #f1f5f9'}}>
                {name === 'check' && canDecide &&
                    <input type="checkbox" checked={selectedUrls.includes(row.product_url)}
                           onChange={() => toggleSelected(row)}/>}
                {name === 'check' && !canDecide && <span style={{width: 13}}/>}
                {row.image && <img referrerPolicy="no-referrer" className="img-fluid-thumb" src={row.image} alt=""/>}
                <div style={{flex: 1, minWidth: 0}}>
                    <div>
                        <a href={row.product_url} target="_blank" rel="noopener noreferrer">{getHostName(row.product_url)}</a>
                        {row.is_match_decision_made && row.is_matched &&
                            <Tag tone={TONE.green}>{t('suggestion.status_matched')}</Tag>}
                        {row.is_match_decision_made && !row.is_matched && !row.removed_during_cleanup &&
                            <Tag tone={TONE.gray}>{t('suggestion.status_unmatched')}</Tag>}
                        {row.removed_during_cleanup &&
                            <Tag tone={TONE.amber}>{t('suggestion.removed_during_cleanup')}</Tag>}
                        {pending &&
                            <Tag tone={TONE.amber} title={t('suggestion.status_not_tracked_hint')}>
                                <span data-testid="not-tracked">{t('suggestion.status_not_tracked')}</span>
                            </Tag>}
                    </div>
                    <div style={{overflowWrap: 'anywhere'}}>{row.name}</div>
                    <div style={{fontSize: '.85rem', color: '#6b7280'}}>
                        {reasons.map((reason, i) => (
                            <span key={i} data-testid="reason"
                                  style={{marginRight: '.75em', color: reason.code === 'all_agree' ? '#15803d' : '#b45309'}}>
                                {reasonText(t, reason)}
                            </span>))}
                    </div>
                </div>
                <div className="p-text-nowrap p-text-right">
                    {row.price && row.price.value ? formatCurrency(row.price.value, row.price.currency) : ''}
                </div>
                <div className="p-text-right" style={{whiteSpace: 'nowrap', minWidth: '7em'}}>
                    {canAccept &&
                        <Button data-testid="accept" icon="pi pi-check-circle" className="p-button-success p-button-sm"
                                tooltip={t('actions.add_as_competitor')} tooltipOptions={{position: 'bottom'}}
                                style={{marginRight: '.5em'}}
                                onClick={() => act(onAccept, [row])}/>}
                    {canReject &&
                        <Button data-testid="reject" icon="pi pi-times-circle" className="p-button-warning p-button-sm"
                                tooltip={t('actions.mark_not_competitor')} tooltipOptions={{position: 'bottom'}}
                                onClick={() => act(onReject, [row])}/>}
                    {row.is_matched && !row.removed_during_cleanup &&
                        <Button data-testid="revert" icon="pi pi-replay" className="p-button-danger p-button-sm"
                                tooltip={t('suggestion.revert_match')} tooltipOptions={{position: 'bottom'}}
                                onClick={() => onRevert(row)}/>}
                    {row.removed_during_cleanup &&
                        <Button data-testid="restore" icon="pi pi-undo" className="p-button-secondary p-button-sm"
                                tooltip={t('suggestion.cleanup_restore')} tooltipOptions={{position: 'bottom'}}
                                onClick={() => onRestore(row)}/>}
                </div>
            </div>
        );
    };

    return (
        <div>
            <ProductSelectionBar
                t={t}
                count={selectedRows.length}
                primaryActions={[
                    {key: 'add', label: t('actions.add_as_competitor'), icon: 'pi pi-check-circle',
                        className: 'p-button-success', command: () => act(onAccept, selectedRows)},
                    {key: 'reject', label: t('actions.mark_not_competitor'), icon: 'pi pi-times-circle',
                        className: 'p-button-warning', command: () => act(onReject, selectedRows)},
                ]}
                onClear={() => setSelectedUrls([])}
            />
            {GROUPS.map(name => {
                const rows = byGroup[name];
                if (rows.length === 0) return null;
                const open = isOpen(name);
                return (
                    <div key={name} data-testid={'group-' + name} style={{marginBottom: '1em'}}>
                        <div style={{display: 'flex', alignItems: 'baseline', gap: '.5em', padding: '.4em .5em'}}>
                            {name === 'ruled_out' &&
                                <button type="button" data-testid="group-toggle" className="p-button p-button-text p-button-sm"
                                        onClick={() => setRuledOutOpen(o => !o)}>
                                    <i className={open ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}/>
                                </button>}
                            <strong>{t('suggestion.group.' + name)}</strong>
                            <span>{rows.length}</span>
                            {name === 'check' &&
                                <span style={{color: '#6b7280', fontSize: '.85rem'}}>{t('suggestion.group.check_hint')}</span>}
                            {name === 'ruled_out' && !open &&
                                <span style={{color: '#6b7280', fontSize: '.85rem'}}>{reasonSummary(t, rows)}</span>}
                        </div>
                        {open && rows.map(row => renderRow(name, row))}
                    </div>
                );
            })}
        </div>
    );
};

export default SuggestionGroups;
